import i18n from "@/plugins/i18n";
/**
 * Extracts the `errorCode` from a ProblemDetails response body surfaced by an axios error.
 * Returns `undefined` for network errors, non-ProblemDetails responses, or any shape mismatch
 * so callers can fall back to a generic message.
 */
export function getApiErrorCode(err) {
    if (err !== null &&
        typeof err === "object" &&
        "response" in err &&
        err.response !== null &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data !== null &&
        typeof err.response.data === "object" &&
        "errorCode" in err.response.data) {
        return String(err.response.data.errorCode);
    }
    return undefined;
}
/**
 * Maps an API error onto the i18n KEY that describes it.
 * Reads the backend `errorCode` and resolves it via the caller-supplied `overrides`
 * map (errorCode → i18n key). Unknown codes — and any non-ProblemDetails error
 * (network, timeout, 500 with no body) — fall back to the `fallback` key.
 *
 * Use this when the consumer translates for you — e.g. `toastError(key)`, which runs `t()`
 * on what it receives. Passing it an already-translated sentence would re-run the message
 * compiler over user-facing copy: a literal `@`, `{` or `|` in that copy then throws
 * `SyntaxError: 10` (R86).
 */
export function resolveApiErrorKey(err, overrides, fallback) {
    const code = getApiErrorCode(err);
    if (code && overrides[code]) {
        return overrides[code];
    }
    // Not every service hoists the failing rule's code to the envelope: OPDOC
    // reports the generic `ValidationError` there and keeps the per-rule codes in
    // `errorResult[]`. Without this the FE could never map a field-level refusal
    // (R70) on those services.
    for (const ruleCode of getValidationRuleErrorCodes(err)) {
        if (overrides[ruleCode]) {
            return overrides[ruleCode];
        }
    }
    return fallback;
}
/**
 * Per-rule `errorCode`s carried by a FluentValidation failure list in the
 * ProblemDetails `errorResult` array. Returns an empty list for any other
 * shape — services that hoist the code (OPINK) put a field→messages object
 * there instead, which must not be mistaken for failures.
 */
export function getValidationRuleErrorCodes(err) {
    if (err !== null &&
        typeof err === "object" &&
        "response" in err &&
        err.response !== null &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data !== null &&
        typeof err.response.data === "object" &&
        "errorResult" in err.response.data) {
        const errorResult = err.response.data.errorResult;
        if (Array.isArray(errorResult)) {
            return errorResult
                .map((failure) => failure !== null && typeof failure === "object" && "errorCode" in failure
                ? failure.errorCode
                : undefined)
                .filter((code) => typeof code === "string");
        }
    }
    return [];
}
/**
 * The raw ProblemDetails `errorResult` slot, whatever shape the service put there.
 *
 * {@link getValidationRuleErrorCodes} reads the FluentValidation array; services also use this
 * slot for structured refusals of their own (OPDON returns the donor's missing tax-receipt fields
 * there). Returns `undefined` for anything that is not a ProblemDetails body, so callers can fall
 * back to a generic message.
 */
export function getApiErrorResult(err) {
    if (err !== null &&
        typeof err === "object" &&
        "response" in err &&
        err.response !== null &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data !== null &&
        typeof err.response.data === "object" &&
        "errorResult" in err.response.data) {
        return err.response.data.errorResult;
    }
    return undefined;
}
/**
 * The failing fields of a validation refusal, keyed by the server's property path
 * (`"SocialIdentifiers.Nir"`). Reads the DICTIONARY shape of `errorResult`, which is how the BFF
 * relays a gRPC service's field violations. The ARRAY shape is read by
 * {@link getValidationRuleErrorCodes}; any other body returns `{}`.
 *
 * The values are whatever the service put there: error codes for a gRPC service, but
 * human-readable messages for a REST one in Development. Callers should rely on the keys; a field
 * left with no string value is dropped.
 */
export function getApiFieldErrors(err) {
    const errorResult = getApiErrorResult(err);
    if (errorResult === null || typeof errorResult !== "object" || Array.isArray(errorResult)) {
        return {};
    }
    const fieldErrors = {};
    for (const [path, codes] of Object.entries(errorResult)) {
        if (!Array.isArray(codes))
            continue;
        const values = codes.filter((code) => typeof code === "string");
        if (values.length > 0)
            fieldErrors[path] = values;
    }
    return fieldErrors;
}
/**
 * Translates an API error into a user-facing message.
 *
 * The returned string is already translated through `i18n.global.t` — hand it to something
 * that renders verbatim (an inline `errorMessage` prop), never to a helper that translates
 * again. When the consumer translates, use {@link resolveApiErrorKey} instead.
 */
export function resolveApiErrorMessage(err, overrides, fallback, params) {
    return i18n.global.t(resolveApiErrorKey(err, overrides, fallback), params ?? {});
}
/**
 * Shared error-code → i18n key map for the "send a test email" modals
 * (template editor + communication action). Both flows hit the same backend
 * guards (`MISSING_PREFERENCES_LINK`, `UNKNOWN_MERGE_FIELD`, `PROVIDER_NOT_CONFIGURED`,
 * `SENDER_EMAIL_REQUIRED`) but expose them under different i18n namespaces; pass the namespace
 * prefix that holds the `error*` keys. A code only ONE of the two flows can raise belongs at that
 * flow's call site, not here.
 *
 * `SENDER_EMAIL_REQUIRED` semantics differ by flow:
 * - Template test: provider's default sender is missing → admin issue.
 * - Action test: `action.SenderEmail` is empty → user must fill the action's
 *   "Sender" section. The dedicated `errorSenderEmailRequired` key lets each
 *   call site phrase the resolution accurately.
 */
export function buildSendTestErrorOverrides(i18nPrefix) {
    return {
        MISSING_PREFERENCES_LINK: `${i18nPrefix}.errorMissingPreferencesLink`,
        UNKNOWN_MERGE_FIELD: `${i18nPrefix}.errorUnknownMergeField`,
        PROVIDER_NOT_CONFIGURED: `${i18nPrefix}.errorProviderNotConfigured`,
        SENDER_EMAIL_REQUIRED: `${i18nPrefix}.errorSenderEmailRequired`,
    };
}
/** What a 429 with no usable delay is assumed to mean, in seconds. */
const DEFAULT_RETRY_AFTER_SECONDS = 60;
/**
 * How long a rate-limited call asks the caller to wait, or `null` when the failure is not a 429.
 *
 * Reads the ProblemDetails `retryAfterSeconds` extension FIRST and the `Retry-After` header only
 * as a fallback, which is the opposite of what the HTTP spec would suggest — for a reason. The
 * portal reaches every service through the BFF, whose shared response handler carries the response
 * BODY across and drops the headers; and even a forwarded header stays invisible to JavaScript
 * unless the BFF also names it in `Access-Control-Expose-Headers`. Reading the header alone
 * therefore always fell through to the constant below, so a ten-minute lockout was announced as a
 * flat minute and the button re-enabled itself eight times too early, each click buying another
 * refusal. The header read is kept for any caller that talks to a service directly.
 *
 * `err` is whatever was thrown, so it can be a non-object: reading `.response` off `null` would
 * replace the real failure with a TypeError raised inside the error handler.
 */
export function getRetryAfterSeconds(err) {
    const response = typeof err === "object" && err !== null
        ? err.response
        : undefined;
    if (response?.status !== 429) {
        return null;
    }
    // Both sources are delta-seconds integers (RFC 9110 §10.2.3 for the header). Axios lowercases
    // response header names.
    for (const candidate of [response.data?.retryAfterSeconds, response.headers?.["retry-after"]]) {
        const seconds = Number(candidate);
        if (Number.isFinite(seconds) && seconds > 0) {
            return Math.ceil(seconds);
        }
    }
    return DEFAULT_RETRY_AFTER_SECONDS;
}
